// @ts-check
import styled from 'styled-components';
import _ from 'lodash';
import { Tooltip } from '@material-ui/core';
import { Flex, Padding } from './base/layout-components';
import Text from './base/Text';
import Icon from './base/Icon';
import * as LighthouseService from 'common/services/lighthouse.service';
import { getColor } from 'common/constants/_colors';
import { StyledWidgetHeader } from './base/common-styles';
import {
  LBAreaChartIcon,
  LBArrowCornerRightUpIcon,
  LBButton,
  LBHelpIcon
} from '@linearb/buzz';

const TabSelected = styled(Text)`
  font-size: 1.6rem;
  margin-right: 1rem;
  padding-bottom: 0.4rem;
  color: ${getColor('dodgerBlue')};
  border-bottom: 1px solid ${getColor('dodgerBlue')};
`;

const TabItem = styled(Text)`
  font-size: 1.6rem;
  margin-right: 1rem;
  color: ${getColor('shuttleGray')};
`;

const StyledSubtitle = styled(Text)`
  size: 1rem;
  weight: 300;
  color: ${getColor('abbey')};
  padding-top: 0.4rem;
  min-height: 2rem;
`;

const TitleWithIcons = ({
  title,
  articleId,
  icons = [],
  additionalIcons = []
}) => {
  return (
    <div className='h-15 py-2 flex flex-row justify-between items-center w-full'>
      {articleId ? (
        <StyledWidgetHeader data-testid='component-title'>
          {title}
        </StyledWidgetHeader>
      ) : (
        <StyledWidgetHeader data-testid='component-title'>
          {title}
        </StyledWidgetHeader>
      )}
      <div
        className='flex flex-row items-center'
        data-testid='component-title-buttons'
      >
        <LBButton
          variant='ghost'
          size='icon'
          onClick={() => LighthouseService.navigateArticle(articleId)}
          data-testid='component-title-helpdocs'
        >
          <LBHelpIcon />
        </LBButton>
        {additionalIcons.map(item => item)}
        {icons.map(({ icon, onClick = _.noop, tooltip }, index) => (
          <Tooltip
            key={`icon-${index}`}
            disableHoverListener={!tooltip}
            title={<Text color='shark'>{tooltip}</Text>}
          >
            <LBButton
              variant='ghost'
              size='icon'
              onClick={onClick}
              data-testid='component-title-button'
            >
              {icon}
            </LBButton>
          </Tooltip>
        ))}
      </div>
    </div>
  );
};

const TitleWithIconsV2 = ({
  title,
  subtitle,
  articleId,
  icons = [],
  additionalIcons = [],
  ...rest
}) => {
  return (
    <Flex width='100%' justify='space-between' items='start' {...rest}>
      <Flex direction='column'>
        {articleId ? (
          <Flex items='flex-end'>
            <Padding r='x-small'>
              <StyledWidgetHeader>{title}</StyledWidgetHeader>
            </Padding>
            <LBButton
              size='icon'
              variant='ghost'
              onClick={() => LighthouseService.navigateArticle(articleId)}
            >
              <Icon name='helpicon' size='smaller' />
            </LBButton>
          </Flex>
        ) : (
          <StyledWidgetHeader>{title}</StyledWidgetHeader>
        )}
        <StyledSubtitle>{subtitle}</StyledSubtitle>
      </Flex>
      <Flex items='center'>
        {additionalIcons.map(item => item)}
        {icons.map(({ icon, onClick = _.noop, tooltip }, index) => (
          <Tooltip
            disableHoverListener={!tooltip}
            key={`icon-${index}`}
            title={tooltip}
          >
            <LBButton size='icon' variant='ghost' onClick={onClick}>
              {icon}
            </LBButton>
          </Tooltip>
        ))}
      </Flex>
    </Flex>
  );
};

export const TitleWithGraphIcon = ({
  title,
  articleId,
  onClick,
  isOpen,
  onLinkIconClick,
  expandChartTooltip,
  showTrends,
  ...rest
}) => (
  <TitleWithIcons
    title={title}
    articleId={articleId}
    icons={[
      ...(showTrends
        ? [
            {
              icon: <LBAreaChartIcon />,
              isOpen,
              onClick,
              tooltip: expandChartTooltip
            }
          ]
        : []),
      ...(onLinkIconClick
        ? [
            {
              icon: <LBArrowCornerRightUpIcon />,
              onClick: onLinkIconClick,
              tooltip: 'Go to Activity'
            }
          ]
        : [])
    ]}
    {...rest}
  />
);

export const TitleWithGraphIconV2 = ({
  title,
  subtitle = '',
  articleId,
  onClick,
  isOpen,
  onLinkIconClick,
  expandChartTooltip,
  tooltipText,
  ...rest
}) => {
  return (
    <TitleWithIconsV2
      title={title}
      subtitle={subtitle}
      articleId={articleId}
      icons={[
        ...(onLinkIconClick
          ? [
              {
                icon: <LBArrowCornerRightUpIcon />,
                onClick: onLinkIconClick,
                tooltip: tooltipText
              }
            ]
          : [])
      ].filter(Boolean)}
      {...rest}
    />
  );
};

export const TitleWithTabs = ({
  tabs,
  titleText,
  selectedTabId,
  onTabClick,
  ...rest
}) => {
  const isSingleTab = tabs.length === 1;
  const tabId = isSingleTab ? tabs[0].id : selectedTabId;
  const selectedTab = tabs.find(({ id }) => id === tabId) || {};
  return (
    <>
      <Flex width='100%' justify='space-between' items='start' {...rest}>
        <Flex items='baseline' data-testid='titleTabs'>
          {isSingleTab ? (
            <TabItem key={selectedTab.id}>{selectedTab.title}</TabItem>
          ) : (
            tabs.map(({ id, title }) => {
              return tabId === id ? (
                <TabSelected key={id}>{title}</TabSelected>
              ) : (
                <LBButton
                  variant='ghost'
                  key={id}
                  onClick={() => onTabClick(id)}
                >
                  {title}
                </LBButton>
              );
            })
          )}
          {titleText && (
            <Text pr='small' color='shuttleGray' data-testid='titleText'>
              {titleText}
            </Text>
          )}
        </Flex>
        {selectedTab.linkTo && (
          <Flex items='center' data-testid='titleTabLink'>
            <Tooltip
              disableHoverListener={!selectedTab.linkTooltip}
              title={
                <Text size='medium' color='shark'>
                  {selectedTab.linkTooltip}
                </Text>
              }
            >
              <LBButton
                size='icon'
                variant='ghost'
                onClick={selectedTab.linkTo}
              >
                <Icon name='linkArrow' color='shuttleGray' />
              </LBButton>
            </Tooltip>
          </Flex>
        )}
      </Flex>
      <Flex items='center' justify='center' pt='medium' height='100%'>
        {isSingleTab
          ? selectedTab.tabPanel
          : tabs.map(({ id, tabPanel }) => {
              return tabId === id ? tabPanel : null;
            })}
      </Flex>
    </>
  );
};
